<script>
import { mapGetters } from 'vuex';
import { convertSecondsToTimeUnit } from '@chatwoot/utils';
import validations from './validations';
import SlaTimeInput from './SlaTimeInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useVuelidate } from '@vuelidate/core';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

export default {
  components: {
    SlaTimeInput,
    NextButton,
    ToggleSwitch,
  },
  props: {
    selectedResponse: {
      type: Object,
      default: () => {},
    },
    submitLabel: {
      type: String,
      required: true,
    },
  },
  emits: ['close', 'submitSla'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      name: '',
      description: '',
      isSlaTimeInputsInvalid: false,
      slaTimeInputsValidation: {},
      slaTimeInputs: [
        {
          threshold: null,
          unit: 'Minutes',
          label: 'SLA.FORM.FIRST_RESPONSE_TIME.LABEL',
          placeholder: 'SLA.FORM.FIRST_RESPONSE_TIME.PLACEHOLDER',
        },
        {
          threshold: null,
          unit: 'Minutes',
          label: 'SLA.FORM.NEXT_RESPONSE_TIME.LABEL',
          placeholder: 'SLA.FORM.NEXT_RESPONSE_TIME.PLACEHOLDER',
        },
        {
          threshold: null,
          unit: 'Minutes',
          label: 'SLA.FORM.RESOLUTION_TIME.LABEL',
          placeholder: 'SLA.FORM.RESOLUTION_TIME.PLACEHOLDER',
        },
      ],
      onlyDuringBusinessHours: false,
      // SiteUp: SLA automático
      autoApply: false,
      priorityEscalation: [],
      priorityLevels: ['medium', 'high', 'urgent'],
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'sla/getUIFlags',
    }),
    isSubmitDisabled() {
      return (
        this.v$.name.$invalid ||
        this.isSlaTimeInputsInvalid ||
        this.uiFlags.isUpdating
      );
    },
    slaNameErrorMessage() {
      let errorMessage = '';
      if (this.v$.name.$error) {
        if (!this.v$.name.required) {
          errorMessage = this.$t('SLA.FORM.NAME.REQUIRED_ERROR');
        } else if (!this.v$.name.minLength) {
          errorMessage = this.$t('SLA.FORM.NAME.MINIMUM_LENGTH_ERROR');
        }
      }
      return errorMessage;
    },
  },
  mounted() {
    if (this.selectedResponse) this.setFormValues();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    setFormValues() {
      const {
        name,
        description,
        first_response_time_threshold: firstResponseTimeThreshold,
        next_response_time_threshold: nextResponseTimeThreshold,
        resolution_time_threshold: resolutionTimeThreshold,
        only_during_business_hours: onlyDuringBusinessHours,
        auto_apply: autoApply,
        priority_escalation: priorityEscalation,
      } = this.selectedResponse;

      this.name = name;
      this.description = description;
      this.onlyDuringBusinessHours = onlyDuringBusinessHours;
      this.autoApply = autoApply || false;
      this.priorityEscalation = Array.isArray(priorityEscalation)
        ? priorityEscalation.map(step => ({ ...step }))
        : [];

      const thresholds = [
        firstResponseTimeThreshold,
        nextResponseTimeThreshold,
        resolutionTimeThreshold,
      ];
      this.slaTimeInputs.forEach((input, index) => {
        const converted = convertSecondsToTimeUnit(thresholds[index], {
          minute: 'Minutes',
          hour: 'Hours',
          day: 'Days',
        });
        input.threshold = converted.time;
        input.unit = converted.unit;
      });
    },
    updateThreshold(index, value) {
      this.slaTimeInputs[index].threshold = value;
    },
    updateUnit(index, unit) {
      this.slaTimeInputs[index].unit = unit;
    },
    onSubmit() {
      const payload = {
        name: this.name,
        description: this.description,
        first_response_time_threshold: this.convertToSeconds(0),
        next_response_time_threshold: this.convertToSeconds(1),
        resolution_time_threshold: this.convertToSeconds(2),
        only_during_business_hours: this.onlyDuringBusinessHours,
        auto_apply: this.autoApply,
        priority_escalation: this.sanitizedEscalation(),
      };
      this.$emit('submitSla', payload);
    },
    sanitizedEscalation() {
      // só degraus válidos (percent 1-100 + prioridade), ordenados por tempo
      return [...this.priorityEscalation]
        .filter(s => Number(s.percent) > 0 && Number(s.percent) <= 100 && s.priority)
        .map(s => ({ percent: Number(s.percent), priority: s.priority }))
        .sort((a, b) => a.percent - b.percent);
    },
    addEscalationStep() {
      // sugere o próximo degrau com base nos já existentes
      const defaults = [
        { percent: 50, priority: 'medium' },
        { percent: 75, priority: 'high' },
        { percent: 90, priority: 'urgent' },
      ];
      const next = defaults[this.priorityEscalation.length] || {
        percent: 90,
        priority: 'urgent',
      };
      this.priorityEscalation.push({ ...next });
    },
    removeEscalationStep(index) {
      this.priorityEscalation.splice(index, 1);
    },
    convertToSeconds(index) {
      const { threshold, unit } = this.slaTimeInputs[index];
      if (threshold === null || threshold === 0) return null;
      const unitsToSeconds = { Minutes: 60, Hours: 3600, Days: 86400 };
      return Number(threshold * (unitsToSeconds[unit] || 1));
    },
    handleIsInvalid(index, isInvalid) {
      this.slaTimeInputsValidation = {
        ...this.slaTimeInputsValidation,
        [index]: isInvalid,
      };

      this.checkValidationState();
    },
    checkValidationState() {
      const isAnyInvalid = Object.values(this.slaTimeInputsValidation).some(
        isInvalid => isInvalid
      );
      this.isSlaTimeInputsInvalid = isAnyInvalid;
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <form class="flex flex-wrap mx-0" @submit.prevent="onSubmit">
      <woot-input
        v-model="name"
        :class="{ error: v$.name.$error }"
        class="w-full"
        :styles="{
          borderRadius: '0.75rem',
          padding: '0.375rem 0.75rem',
          fontSize: '0.875rem',
        }"
        :label="$t('SLA.FORM.NAME.LABEL')"
        :placeholder="$t('SLA.FORM.NAME.PLACEHOLDER')"
        :error="slaNameErrorMessage"
        @input="v$.name.$touch"
        @blur="v$.name.$touch"
      />
      <woot-input
        v-model="description"
        class="w-full"
        :styles="{
          borderRadius: '0.75rem',
          padding: '0.375rem 0.75rem',
          fontSize: '0.875rem',
        }"
        :label="$t('SLA.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('SLA.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <SlaTimeInput
        v-for="(input, index) in slaTimeInputs"
        :key="index"
        :threshold="input.threshold"
        :threshold-unit="input.unit"
        :label="$t(input.label)"
        :placeholder="$t(input.placeholder)"
        @update-threshold="updateThreshold(index, $event)"
        @unit="updateUnit(index, $event)"
        @is-in-valid="handleIsInvalid(index, $event)"
      />

      <div
        class="mt-3 flex h-10 items-center text-sm w-full gap-2 border border-solid border-n-strong px-3 py-1.5 rounded-2xl justify-between"
      >
        <span for="sla_bh" class="text-n-slate-11">
          {{ $t('SLA.FORM.BUSINESS_HOURS.PLACEHOLDER') }}
        </span>
        <ToggleSwitch id="sla_bh" v-model="onlyDuringBusinessHours" />
      </div>

      <!-- SiteUp: aplicar automaticamente a novas conversas -->
      <div
        class="mt-3 flex h-10 items-center text-sm w-full gap-2 border border-solid border-n-strong px-3 py-1.5 rounded-2xl justify-between"
      >
        <span for="sla_auto" class="text-n-slate-11">
          Aplicar automaticamente a novas conversas
        </span>
        <ToggleSwitch id="sla_auto" v-model="autoApply" />
      </div>

      <!-- SiteUp: escalonamento automático de prioridade -->
      <div
        class="w-full p-3 mt-3 border border-solid border-n-strong rounded-2xl"
      >
        <div class="flex items-start justify-between gap-2">
          <div>
            <p class="m-0 text-sm font-medium text-n-slate-12">
              Escalonar prioridade automaticamente
            </p>
            <p class="m-0 mt-0.5 text-xs text-n-slate-10">
              Conforme o prazo corre, sobe a prioridade da conversa (acende as
              bordas de urgência).
            </p>
          </div>
          <NextButton
            faded
            slate
            sm
            type="button"
            icon="i-ri-add-line"
            label="Degrau"
            @click.prevent="addEscalationStep"
          />
        </div>

        <div
          v-if="priorityEscalation.length"
          class="flex flex-col gap-2 mt-3"
        >
          <div
            v-for="(step, index) in priorityEscalation"
            :key="index"
            class="flex items-center gap-2"
          >
            <span class="text-xs text-n-slate-10 w-9">Ao</span>
            <div class="flex items-center gap-1">
              <input
                v-model.number="step.percent"
                type="number"
                min="1"
                max="100"
                class="w-16 px-2 py-1.5 text-sm rounded-xl bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
              />
              <span class="text-sm text-n-slate-11">% do prazo →</span>
            </div>
            <select
              v-model="step.priority"
              class="flex-1 px-2 py-1.5 text-sm rounded-xl bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
            >
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
            <button
              type="button"
              class="grid rounded-md size-7 place-items-center text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-ruby-11"
              @click.prevent="removeEscalationStep(index)"
            >
              <span class="size-4 i-ri-delete-bin-line" />
            </button>
          </div>
        </div>
        <p v-else class="m-0 mt-2 text-xs italic text-n-slate-10">
          Nenhum degrau. Sugestão: 50%→Média, 75%→Alta, 90%→Urgente.
        </p>
      </div>

      <div class="flex items-center justify-end w-full gap-2 mt-8">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('SLA.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="submitLabel"
          :disabled="isSubmitDisabled"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>
